import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Pencil, Save, X as XIcon, Loader2, IdCard, ChevronDown, Check } from "lucide-react";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";

const EMPTY_BIO = {
  grad_year: "", birthdate: "", height: "", weight: "",
  hs_name: "", city: "", state: "",
  aau_program: "",
  hs_coach_name: "", hs_coach_contact: "",
  parent_name: "", parent_contact: "",
  gpa: "", sat: "", act: "",
  package_name: "", package_status: "", package_start_date: "",
};

const PACKAGE_OPTIONS = [
  "",
  "Mentorship",
  "Year Long Tier 1",
  "Year Long Tier 2",
  "Year Long Custom",
  "The Program Platinum",
];
const PACKAGE_STATUS = ["", "Active", "Paused", "Ended"];

const FIELD_GROUPS = [
  {
    title: "Package",
    fields: [
      { k: "package_name", label: "Package", type: "select", options: PACKAGE_OPTIONS },
      { k: "package_status", label: "Status", type: "select", options: PACKAGE_STATUS },
      { k: "package_start_date", label: "Start Date", type: "date" },
    ],
  },
  {
    title: "Player",
    fields: [
      { k: "grad_year", label: "Grad Year", placeholder: "2027" },
      { k: "birthdate", label: "Birthdate", type: "date" },
      { k: "height", label: "Height", placeholder: "6-3" },
      { k: "weight", label: "Weight", placeholder: "185" },
    ],
  },
  {
    title: "School",
    fields: [
      { k: "hs_name", label: "High School", placeholder: "Roselle Catholic" },
      { k: "city", label: "City", placeholder: "Roselle" },
      { k: "state", label: "State", placeholder: "NJ" },
      { k: "aau_program", label: "AAU / Club Team", placeholder: "NJ Playaz" },
    ],
  },
  {
    title: "Contacts",
    fields: [
      { k: "hs_coach_name", label: "HS Coach", placeholder: "Dave Boff" },
      { k: "hs_coach_contact", label: "Coach Contact", placeholder: "phone / email" },
      { k: "parent_name", label: "Parent / Guardian", placeholder: "Full name" },
      { k: "parent_contact", label: "Parent Contact", placeholder: "phone / email" },
    ],
  },
  {
    title: "Academics",
    fields: [
      { k: "gpa", label: "GPA", placeholder: "3.5" },
      { k: "sat", label: "SAT", placeholder: "1200" },
      { k: "act", label: "ACT", placeholder: "25" },
    ],
  },
];

export default function PlayerBio({ playerId, initialBio }) {
  const { user } = useAuth();
  const canEdit = isAdmin(user);
  const [bio, setBio] = useState({ ...EMPTY_BIO, ...(initialBio || {}) });
  const [draft, setDraft] = useState(bio);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBio({ ...EMPTY_BIO, ...(initialBio || {}) });
  }, [initialBio]);

  const startEdit = () => { setDraft({ ...bio }); setEditing(true); };
  const cancel = () => { setEditing(false); };

  const save = async () => {
    setSaving(true);
    try {
      const cleaned = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, v === "" ? null : v]));
      const saved = await api.updatePlayerBio(playerId, cleaned);
      setBio({ ...EMPTY_BIO, ...saved });
      setEditing(false);
      toast.success("Bio saved");
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const isEmpty = FIELD_GROUPS.every(g => g.fields.every(f => !bio[f.k]));

  return (
    <section className="border border-white/10 bg-[#101010]" data-testid="player-bio">
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <IdCard className="w-4 h-4 text-[var(--org-accent)]" />
          <h3 className="font-heading uppercase text-lg tracking-[0.2em]">Player Bio</h3>
        </div>
        {canEdit && !editing && (
          <button
            onClick={startEdit}
            className="flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-[10px] uppercase tracking-[0.3em] font-bold hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"
            style={{ transition: "color 0.2s ease, border-color 0.2s ease" }}
            data-testid="bio-edit-btn"
          >
            <Pencil className="w-3 h-3" /> {isEmpty ? "Fill In" : "Edit"}
          </button>
        )}
        {canEdit && editing && (
          <div className="flex items-center gap-2">
            <button
              onClick={cancel}
              className="flex items-center gap-2 px-4 py-2 border border-white/20 text-[#a0a0a0] text-[10px] uppercase tracking-[0.3em] font-bold hover:text-white"
              data-testid="bio-cancel-btn"
            >
              <XIcon className="w-3 h-3" /> Cancel
            </button>
            <button
              disabled={saving}
              onClick={save}
              className="flex items-center gap-2 px-4 py-2 bg-[var(--org-accent)] text-[#121212] text-[10px] uppercase tracking-[0.3em] font-bold hover:bg-[var(--org-accent-hover)] disabled:opacity-60"
              data-testid="bio-save-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </button>
          </div>
        )}
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
        {FIELD_GROUPS.map(group => (
          <div key={group.title}>
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#a0a0a0] mb-3">{group.title}</div>
            <div className="grid grid-cols-2 gap-3">
              {group.fields.map(f => (
                <div key={f.k}>
                  <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-1">{f.label}</div>
                  {editing ? (
                    f.type === "select" ? (
                      <CustomSelect
                        value={draft[f.k] || ""}
                        options={f.options}
                        onChange={(v) => setDraft(d => ({ ...d, [f.k]: v }))}
                        testId={`bio-input-${f.k}`}
                      />
                    ) : (
                      <input
                        type={f.type || "text"}
                        value={draft[f.k] || ""}
                        onChange={(e) => setDraft(d => ({ ...d, [f.k]: e.target.value }))}
                        placeholder={f.placeholder}
                        className="w-full bg-[#141414] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)]"
                        data-testid={`bio-input-${f.k}`}
                      />
                    )
                  ) : (
                    <div className="text-white text-sm min-h-[34px] flex items-center px-3 border-b border-white/5" data-testid={`bio-value-${f.k}`}>
                      {bio[f.k] || <span className="text-[#555]">—</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* Custom dropdown that renders above the sticky navbar and isn't clipped by
   the tab section — the native <select>'s option list was appearing behind
   the tabs on some browsers/screens. */
function CustomSelect({ value, options, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 bg-[#141414] border border-white/10 px-3 py-2 text-sm text-white focus:outline-none focus:border-[var(--org-accent)] hover:border-white/30"
        data-testid={testId}
      >
        <span className={value ? "text-white" : "text-[#575757]"}>{value || "—"}</span>
        <ChevronDown className="w-3.5 h-3.5 text-[#a0a0a0] shrink-0" />
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-full mt-1 bg-[#141414] border border-white/15 shadow-2xl z-[60] max-h-[280px] overflow-auto">
          {options.map(opt => (
            <button
              key={opt}
              type="button"
              onClick={() => { onChange(opt); setOpen(false); }}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-white/[0.05] ${opt === value ? "text-[var(--org-accent)]" : "text-white"}`}
              data-testid={`${testId}-opt-${(opt || 'none').replace(/\s+/g, '-')}`}
            >
              <span className={opt ? "" : "italic text-[#757575]"}>{opt || "— None —"}</span>
              {opt === value && <Check className="w-3.5 h-3.5 shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
